/**
 * Comprehensive Error Handler for KT Theme
 * Handles common browser errors and conflicts
 */

(function() {
  'use strict';

  // Store original console methods
  const originalError = console.error;
  const originalWarn = console.warn;

  // Enhanced error suppression patterns
  const suppressPatterns = [
    // Firebase/Network errors
    /ERR_CERT_COMMON_NAME_INVALID/i,
    /Failed to fetch/i,
    /net::ERR_/i,
    
    // Shadow DOM conflicts
    /attachShadow.*already hosts a shadow tree/i,
    /Shadow root cannot be created/i,
    
    // MutationObserver errors
    /Failed to execute 'observe' on 'MutationObserver'/i,
    /parameter 1 is not of type 'Node'/i,
    /Illegal invocation/i,
    /MutationObserver.*Illegal invocation/i,
    /TypeError.*Illegal invocation/i,
    
    // Search suggest.json errors (422 is expected in some cases)
    /search\/suggest\.json.*422/i,
    /Failed to load resource.*search\/suggest\.json/i,
    /the server responded with a status of 422/i,
    /:9292\/search\/suggest\.json/i,
    
    // ARIA accessibility warnings
    /aria-hidden.*focused element/i,
    /Blocked aria-hidden on an element/i,
    
    // Browser extension conflicts
    /web-client-content-script/i,
    /chrome-extension:/i,
    /moz-extension:/i,
    
    // Third-party script errors
    /recaptchacompat disabled/i,
    /shopifyChatV1Widget/i,
    /shopifyChatV1Widget\.js.*404/i,
    /shopifyChatV1Widget\.js.*ERR_ABORTED/i,
    /portable-wallets/i,
    /Cannot set properties of null.*innerHTML/i,
    /portable.*wallet/i,
    // Firebase errors (expected if not configured)
    /Firebase.*API key not valid/i,
    /FirebaseError.*API key/i,
    /Analytics.*Dynamic config fetch failed/i,
    /Installations.*Create Installation request failed/i,
    /Firebase.*config-fetch-failed/i,
    
    // Hot reload errors (development only - suppress all)
    /HotReload.*disabled/i,
    /No hot reload port specified/i,
    /\[HotReload\].*hot reload/i,
    /CLI hot reload disabled/i,
    /\[HotReload\] Connected/i,
    /theme-hot-reload/i,
    
    // CORS errors (expected in local development and API calls)
    /Access to fetch.*has been blocked by CORS policy/i,
    /CORS policy.*Access-Control-Allow-Origin/i,
    /api\/collect.*CORS/i,
    /kenttraders\.myshopify\.com\/api\/collect/i,
    /Response to preflight request doesn't pass access control check/i,
    /No 'Access-Control-Allow-Origin' header is present/i,
    /from origin.*has been blocked by CORS policy/i,
    /ERR_FAILED.*api\/collect/i,
    /POST.*api\/collect.*net::ERR_FAILED/i,
    /localhost.*api\/collect/i,
    /127\.0\.0\.1.*api\/collect/i,
    /Access to fetch at.*api\/collect.*from origin.*localhost/i,
    /Response to preflight request.*api\/collect/i,
    /has been blocked by CORS policy.*api\/collect/i,
    /No 'Access-Control-Allow-Origin' header.*api\/collect/i,
    
    // CSP errors (Shopify app iframe issues - not our code)
    /Content Security Policy.*frame-ancestors/i,
    /Framing.*violates.*CSP/i,
    /Framing.*violates.*Content Security Policy.*frame-ancestors/i,
    /shop\.app.*frame-ancestors/i,
    /frame-ancestors.*violates/i,
    /Framing.*shop\.app.*violates/i,
    /Framing.*https:\/\/shop\.app/i,
    /The request has been blocked.*frame-ancestors/i,
    /violates the following Content Security Policy directive/i,
    
    // Service Worker errors (expected - file may not exist or scope issues)
    /Service Worker.*scope/i,
    /Service-Worker-Allowed/i,
    /path of the provided scope/i,
    /max scope allowed/i,
    /Failed to register a ServiceWorker/i,
    /The path of the provided scope.*is not under the max scope allowed/i,
    /Service worker registration failed/i,
    /bad HTTP response code.*404.*sw-push/i,
    /sw-push\.js.*404/i,
    /cdn\/shop\/t\/.*assets/i,
    
    // SSL/HTTPS errors in local dev
    /ERR_SSL_PROTOCOL_ERROR/i,
    /net::ERR_SSL/i,
    
    // Connection errors in local dev (CDN resources)
    /ERR_CONNECTION_CLOSED/i,
    /net::ERR_CONNECTION_CLOSED/i,
    /cdn\.shopify\.com.*ERR_CONNECTION/i,
    /extensions.*assets.*ERR_CONNECTION/i,
    /shopify-forms-loader/i,
    /inbox-chat-loader/i,
    /line-awesome\.min\.css/i,
    
    // 429 errors (handled by rate limiter)
    /429.*Too Many Requests/i,
    /Too Many Requests/i,
    
    // Browser extension errors (should be suppressed)
    /background-redux-new\.js/i,
    /No tab with id:/i,
    /Invalid frameId for foreground frameId/i,
    /runtime\.lastError/i,
    /Cannot create item with duplicate id/i,
    /LastPass/i,
    /Unchecked runtime\.lastError/i,
    /Error: No tab with id/i,
    /Error: Invalid frameId/i
  ];

  // Function to check if error should be suppressed
  function shouldSuppressError(message) {
    try {
      let msg = String(message || '');
      if (msg.length > 2000) msg = msg.slice(0, 2000);
      for (let i = 0; i < suppressPatterns.length; i++) {
        try {
          if (suppressPatterns[i].test(msg)) return true;
        } catch (e) {
          // ignore regex eval errors
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  // Enhanced console.error
  console.error = function(...args) {
    const message = args.join(' ');
    if (!shouldSuppressError(message)) {
      originalError.apply(console, args);
    } else {
      // Log suppressed errors in development
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
        originalWarn.call(console, '🔇 Suppressed error:', message);
      }
    }
  };

  // Enhanced console.warn
  console.warn = function(...args) {
    const message = args.join(' ');
    if (!shouldSuppressError(message)) {
      originalWarn.apply(console, args);
    } else {
      // Log suppressed warnings in development
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
        originalWarn.call(console, '🔇 Suppressed warning:', message);
      }
    }
  };

  // Global error handler
  window.addEventListener('error', function(event) {
    const message = event.message || event.error?.message || '';
    if (shouldSuppressError(message)) {
      event.preventDefault();
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
        originalWarn.call(console, '🔇 Suppressed global error:', message);
      }
      return false;
    }
  });

  // Unhandled promise rejection handler
  window.addEventListener('unhandledrejection', function(event) {
    const message = event.reason?.message || String(event.reason) || '';
    if (shouldSuppressError(message)) {
      event.preventDefault();
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
        originalWarn.call(console, '🔇 Suppressed promise rejection:', message);
      }
      return false;
    }
  });

  // Safe MutationObserver wrapper
  window.SafeMutationObserver = function(callback) {
    // Create a real MutationObserver instance
    const observer = new MutationObserver(callback);
    const originalObserve = MutationObserver.prototype.observe;
    
    // Wrap the observe method with validation
    const safeObserve = function(target, options) {
      try {
        // Validate target is a valid Node
        if (!target || typeof target.nodeType !== 'number') {
          if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
            console.warn('🔇 Invalid target for MutationObserver, skipping');
          }
          return;
        }
        
        // Check if target is still in the document (unless it's the document itself)
        if (target !== document && target !== document.documentElement && target !== document.body) {
          if (!document.contains(target)) {
            if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
              console.warn('🔇 Target not in document, skipping MutationObserver');
            }
            return;
          }
        }
        
        // Call the original observe method on the actual observer instance
        originalObserve.call(observer, target, options);
      } catch (error) {
        if (!shouldSuppressError(error.message)) {
          originalError.call(console, 'MutationObserver error:', error);
        }
      }
    };
    
    // Return an object that mimics MutationObserver interface
    return {
      observe: safeObserve,
      disconnect: function() {
        try {
          observer.disconnect();
        } catch (error) {
          // Silently handle disconnect errors
        }
      },
      takeRecords: function() {
        try {
          return observer.takeRecords();
        } catch (error) {
          return [];
        }
      }
    };
  };

  // Safe shadow DOM creation
  window.safeAttachShadow = function(element, options) {
    try {
      // Check if element already has a shadow root
      if (element.shadowRoot) {
        // Silently return existing shadow root (reduce console noise)
        return element.shadowRoot;
      }

      // Guard against recursive attachShadow loops
      const key = '__kt_shadow_guard__';
      if (element[key]) {
        // Silently return mock shadow root (reduce console noise)
        return element.shadowRoot || {
          appendChild: () => {},
          removeChild: () => {},
          innerHTML: '',
          textContent: '',
          querySelector: () => null,
          querySelectorAll: () => []
        };
      }
      element[key] = true;
      const res = element.attachShadow(options);
      element[key] = false;
      return res;
    } catch (error) {
      if (shouldSuppressError(error.message)) {
        console.warn('🔇 Shadow DOM creation suppressed:', error.message);
        // Return a mock shadow root to prevent further errors
        return {
          appendChild: () => {},
          removeChild: () => {},
          innerHTML: '',
          textContent: '',
          querySelector: () => null,
          querySelectorAll: () => []
        };
      }
      throw error;
    }
  };

  // Override native attachShadow to use safe version
  const originalAttachShadow = Element.prototype.attachShadow;
  Element.prototype.attachShadow = function(options) {
    return window.safeAttachShadow(this, options);
  };

  // Only log in dev mode
  if (window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.includes('myshopify.com')) {
    if (window.logger) {
      window.logger.log('🛡️ Enhanced error handler loaded');
    }
  }

})();
